import * as Yup from "yup";
import { ACCOUNT_MAP_ICON_SIZE_DEFAULT_VALUE } from "../../../constants/constants";
import { phoneRegex } from "../../../utils/validation";

// initial values for the form
export const accountDetailsInitialValues = {
  account_id: "",
  account_description: "",
  contact_name: "",
  contact_phone_number: "",
  contact_email: "",
  private_cost: 0.0,
  // idle_gas_usage: 0.0,
  // distance_gas_usage: 0.0,
  auto_update_interval_for_maps: 0,
  drivers_assigned_to_devices: false,
  enable_map_clustering: 0,
  open_reports_in_new_tab: false,
  sync_driverId_from_driver_admin: false,
  has_snowplows: false,
  hide_total_rows_in_csv: false,
  timezone: "",
  speed_units: "0",
  distance_units: "0",
  volume_units: "0",
  economy_units: "0",
  pressure_units: "0",
  temperature_units: "0",
  cameraliveviewduration: 30,
  minidleseconds: 0,
  latitude_longitude_format: "0",
  // route_segment_color_rule: "", // field removed per client request
  route_line_thickness: "",
  multi_vehicle_map_name: "",
  device_title: "",
  device_title_plural: "",
  device_group_title: "",
  device_group_title_plural: "",
  address_title: "",
  address_title_plural: "",
  // default_login_userId: "", // field removed per client request
  default_overlay: "",
  last_maintenance_1: "",
  last_maintenance_2: "",
  last_maintenance_3: "",
  last_maintenance_4: "",
  last_maintenance_5: "",
  last_maintenance_6: "",
  last_maintenance_7: "",
  last_maintenance_8: "",
  last_maintenance_9: "",
  last_maintenance_10: "",
  last_eng_hours_maint_1: "",
  last_eng_hours_maint_2: "",
  last_eng_hours_maint_3: "",
  last_eng_hours_maint_4: "",
  last_eng_hours_maint_5: "",
  last_service_time_1: "",
  last_service_time_2: "",
  last_service_time_3: "",
  last_service_time_4: "",
  last_service_time_5: "",
  map_icon_size: ACCOUNT_MAP_ICON_SIZE_DEFAULT_VALUE,
  harsh_braking: "",
  harsh_acceleration: "",
  speeding: "",
  reverse: "",
  seatbelt_off: "",
  harsh_cornering: "",
  idle_ratio: "",
  impact_crash_ai: "",
  cellphone_use_ai: "",
  distracted_driving_ai: "",
  drinking_eating_ai: "",
  smoking_ai: "",
  possible_fatiuge_ai: "",
  obstructed_camera_ai: "",
  tailgating_ai: "",
  scorewtfrontcollisionai: "",
  scorewtpedestriancollisionai: "",
  scorewtlanedepartureai: "",
  scorewtstopandgoai: "",
  scorewtrollingstopai: "",
  scorewtcamerapowerlossai: "",
  scorewtcameracoverremovalai: "",
  scorewt75for30ai: "",
  // toggleft: false, // only sysadmin has access to this field
  use_2fa: false,
  infobox_always_open: false,
  hide_asset_map_pinpoints: false,
  infobox_asset_id: true,
  infobox_asset_desc: false,
  infobox_asset_display: false,
  infobox_driver: false,
  // asset label configuration fields
  asset_label_fields: [] as string[],
  asset_searchable_fields: [] as string[],
};

// validation schema for the form
export const accountDetailsYupValidationSchema = Yup.object().shape({
  account_id: Yup.string().required("Account ID is required"),
  account_description: Yup.string().required("Account Description is required"),
  contact_phone_number: Yup.string().matches(phoneRegex, "Invalid phone"),
  private_cost: Yup.number()
    .typeError("Please enter a numeric value")
    .test(
      "max-2-decimals",
      "Private cost can have at most 2 decimal places",
      (value) => {
        if (value === undefined || value === null || (value as any) === "")
          return true;
        // Check if value is integer or has at most 2 decimal places
        return /^\d+(\.\d{1,2})?$/.test(value.toString());
      }
    ),
  // idle_gas_usage: Yup.number().required("Idle Gas Usage is required"),
  // distance_gas_usage: Yup.number().required("Distance Gas Usage is required"),
  auto_update_interval_for_maps: Yup.number()
    .required("Auto Update Interval For Maps is required")
    .integer("Whole number required"),
  drivers_assigned_to_devices: Yup.boolean().required(
    "Drivers Assigned To Devices is required"
  ),
  enable_map_clustering: Yup.string().required(
    "Enable Map Clustering is required"
  ),
  open_reports_in_new_tab: Yup.boolean().required(
    "Open Reports In New Tab is required"
  ),
  sync_driverId_from_driver_admin: Yup.boolean().required(
    "Sync DriverId From Driver Admin is required"
  ),
  has_snowplows: Yup.boolean().required("Has Snowplows is required"),
  hide_total_rows_in_csv: Yup.boolean().required(
    "Hide Total Rows In CSV is required"
  ),
  timezone: Yup.string().required("Timezone is required"),
  speed_units: Yup.string().required("Speed Units is required"),
  distance_units: Yup.string().required("Distance Units is required"),
  volume_units: Yup.string().required("Volume Units is required"),
  economy_units: Yup.string().required("Economy Units is required"),
  pressure_units: Yup.string().required("Pressure Units is required"),
  temperature_units: Yup.string().required("Temperature Units is required"),
  minidleseconds: Yup.number().typeError("Minimum Idle Minutes must be a number").required("Minimum Idle Minutes is required")
    .integer("Value must be a whole number")
    .min(0, "Value must be at least 0").max(60, "Value must be at most 60"),
  cameraliveviewduration: Yup.number().typeError("Camera Live View Duration must be a number").required("Camera Live View Duration is required")
    .min(10, "Value must be at least 10 seconds").max(600, "Value must be at most 10 minutes"),
  latitude_longitude_format: Yup.string().required(
    "Latitude Longitude Format is required"
  ),
  last_maintenance_1: Yup.string().required("Last Maintenance 1 is required"),
  last_maintenance_2: Yup.string().required("Last Maintenance 2 is required"),
  last_maintenance_3: Yup.string().required("Last Maintenance 3 is required"),
  last_maintenance_4: Yup.string().required("Last Maintenance 4 is required"),
  last_maintenance_5: Yup.string().required("Last Maintenance 5 is required"),
  last_maintenance_6: Yup.string().required("Last Maintenance 6 is required"),
  last_maintenance_7: Yup.string().required("Last Maintenance 7 is required"),
  last_maintenance_8: Yup.string().required("Last Maintenance 8 is required"),
  last_maintenance_9: Yup.string().required("Last Maintenance 9 is required"),
  last_maintenance_10: Yup.string().required("Last Maintenance 10 is required"),
  last_eng_hours_maint_1: Yup.string().required(
    "Last Eng Hours Maint 1 is required"
  ),
  last_eng_hours_maint_2: Yup.string().required(
    "Last Eng Hours Maint 2 is required"
  ),
  last_eng_hours_maint_3: Yup.string().required(
    "Last Eng Hours Maint 3 is required"
  ),
  last_eng_hours_maint_4: Yup.string().required(
    "Last Eng Hours Maint 4 is required"
  ),
  last_eng_hours_maint_5: Yup.string().required(
    "Last Eng Hours Maint 5 is required"
  ),
  last_service_time_1: Yup.string().required("Last Service Time 1 is required"),
  last_service_time_2: Yup.string().required("Last Service Time 2 is required"),
  last_service_time_3: Yup.string().required("Last Service Time 3 is required"),
  last_service_time_4: Yup.string().required("Last Service Time 4 is required"),
  last_service_time_5: Yup.string().required("Last Service Time 5 is required"),
  harsh_braking: Yup.string().required("Harsh Braking is required"),
  harsh_acceleration: Yup.string().required("Harsh Acceleration is required"),
  speeding: Yup.string().required("Speeding is required"),
  reverse: Yup.string().required("Reverse is required"),
  seatbelt_off: Yup.string().required("Seatbelt Off is required"),
  harsh_cornering: Yup.string().required("Harsh Cornering is required"),
  idle_ratio: Yup.string().required("Idle Ratio is required"),
  impact_crash_ai: Yup.string().required("Impact Crash AI is required"),
  cellphone_use_ai: Yup.string().required("Cellphone Use AI is required"),
  distracted_driving_ai: Yup.string().required(
    "Distracted Driving AI is required"
  ),
  drinking_eating_ai: Yup.string().required("Drinking Eating AI is required"),
  smoking_ai: Yup.string().required("Smoking AI is required"),
  possible_fatiuge_ai: Yup.string().required("Possible Fatiuge AI is required"),
  obstructed_camera_ai: Yup.string().required(
    "Obstructed Camera AI is required"
  ),
  tailgating_ai: Yup.string().required("Tailgating AI is required"),
  infobox_always_open: Yup.boolean(),
  hide_asset_map_pinpoints: Yup.boolean(),
  infobox_asset_id: Yup.boolean(),
  infobox_asset_desc: Yup.boolean(),
  infobox_asset_display: Yup.boolean(),
  infobox_driver: Yup.boolean(),
  // asset label configuration fields - atleast one label should be selected
  asset_label_fields: Yup.array()
    .of(Yup.string())
    .ensure()
    .compact()
    .min(1, "At least one component must be selected"),
  asset_searchable_fields: Yup.array()
    .of(Yup.string())
    .ensure()
    .compact()
    .min(1, "At least one searchable field must be selected"),
});
